import React, { useEffect, useState } from 'react';
import Completed from './assets/icon-completed.svg';
import Close from './assets/icon-close.svg';
import uniqueId from 'lodash/uniqueId';
import filesize from 'filesize';
import Axios from 'axios';
import './upload-progress.scss';
import { ListFile, ListDropzone, FileDropzone } from './types-uploader';
import Modal from 'components/commons/modal';
import Error from 'components/commons/error';
import { DocumentUploadS3 } from 'generated/credit-analysis-graphql';
import PDFIcon from 'assets/svg/pdf-icon.svg';

type UploadItemProps = {
   filesDropzone: ListDropzone;
   submit?: boolean;
   onComplete(): void;
   onError(file: ListFile): void;
   getSignedUrl(filename: string, contentType: string): Promise<DocumentUploadS3>;
   removeDropZone(src: string): void;
};

type Progress = {
   [prop: string]: number,
};

const UploadItem: React.FC<UploadItemProps> = ({ getSignedUrl, onComplete, onError, filesDropzone, submit, removeDropZone }): JSX.Element => {
   const [source] = useState(Axios.CancelToken.source());
   const [data, setData] = useState<ListFile>([]);
   const [progress, setProgress] = useState<Progress>({});
   const [visible, setVisible] = useState(false);
   const [loading, setLoading] = useState(false);
   const [errorMessages, setErrorMessages] = useState([]);
   const [selected, setSelected] = useState<FileDropzone>({ name: '', src: '', size: '', type: '' });

   useEffect(() => {
      sendToServer();
   }, [submit]);

   useEffect(() => {
      loadItens();
   }, [filesDropzone]);

   /**
    * função responsavel por enviar os arquivos anexados.
    * @param {FileDropzone} documentFile - arquivo que sera enviado
    * @param {ListFile} newFiles -lista de arquivos enviados
    * @param {number} index - index referente a posição do arquivo em filesDropzone
    * @return {ListFile} lista de arquivos enviados com sucesso ou não
    */
   const upload = async (documentFile: FileDropzone, newFiles: ListFile, index: number) => {
      const promiseFiles = newFiles;
      const errors = errorMessages;
      setErrorMessages([]);
      try {
         const response = await getSignedUrl(documentFile.name, documentFile.type);
         await Axios.put(response.url, documentFile.file, {
            headers: {
               'Content-Type': documentFile.type,
               'x-amz-meta-data': response.data,
            },
            cancelToken: source.token,
            onUploadProgress: ({ loaded, total }: ProgressEvent) => {
               setProgress({});
               const progressTotal = Math.round((loaded / total) * 100);
               const newArr = progress;
               newArr[documentFile.name] = progressTotal;
               setProgress(newArr);
            },
         });
         promiseFiles[index].progress = 100;
         promiseFiles[index].completed = true;
         promiseFiles[index].error = false;
      } catch (error) {
         promiseFiles[index].error = true;
         promiseFiles[index].file = documentFile.file;
         promiseFiles[index].progress = 0;
         promiseFiles[index].completed = false;
         errors.push(`Não foi possível enviar o arquivo ${documentFile.name}`);
         setErrorMessages(errors);
      }
      return promiseFiles;
   }

   /**
    * função chamada ao clicar em enviar.
    */
   const sendToServer = async () => {
      // verifica se ao clicar em não tem novos arquivos e algum arquivo ja foi enviado anteriormente
      if (filesDropzone.length === 0 && data.filter(d => d.error === false).length > 0) {
         return onComplete();
      }

      if (filesDropzone.length) {
         setLoading(true);
         const promises = [];

         for (let index = 0; index < filesDropzone.length; index++) {
            if (!data.find(d => d.completed === true && d.src === filesDropzone[index].src)) {
               promises.push(upload(filesDropzone[index], data, index));
            }
         }

         if (promises.length) {
            Promise.all(promises)
               .then((files) => {
                  setLoading(false);
                  if (files[0].find(x => x.error === true)) {
                     const value: ListFile = files[0];
                     onError(value);
                  } else {
                     onComplete();
                  }
               })
         } else {
            onComplete();
         }
      }
   }

   // responsavel por verificar e atualizar a lista de arquivos ao anexar um novo arquivo
   const loadItens = () => {
      const newFiles = filesDropzone.filter(file => !data.find(d => d.src === file.src)).map(f => {
         return {
            id: uniqueId(),
            name: f.name,
            progress: 0,
            size: filesize(f.file.size),
            completed: false,
            error: false,
            src: f.src,
            type: f.type
         };
      });
      setData([...data, ...newFiles]);
   }

   const removeItem = (id: string, src: string) => {
      setData(data.filter(d => d.id !== id));
      removeDropZone(src);
   }

   const openModal = (attachment) => {
      setSelected({
         name: attachment.name,
         src: attachment.src,
         size: attachment.size.toLocaleString(),
         type: attachment.type,
      });
      setVisible(true);
   }

   return (
      <>
         {errorMessages.map(error => <Error key={error} message={error} />)}
         {data.map((file) => {
            if (file.completed) {
               return (
                  <div key={'item-' + file.id} className="c-progress-upload c-progress-upload--completed">
                     <div className="c-progress-upload__title body-2">{file.name} {file.error ? 'ERRO' : 'ok'} </div>
                     <div className="c-progress-upload__size overline">{file.size}</div>
                     <div className="c-progress-upload__actions">
                        <span><img src={Completed} /></span>
                     </div>
                  </div>
               )
            } else {
               return (
                  <div key={'item-' + file.id} className={`c-progress-upload ${loading ? 'loading' : 'awaiting'}`}>
                     <div className="c-progress-upload__title body-2">
                        <a onClick={() => openModal(file)}>
                           {file.name}
                           <div className="c-progress-upload__size overline" style={{ display: `${loading ? 'none' : 'block'}` }} >
                              {file.size}
                           </div>
                        </a>
                     </div>
                     <div
                        style={{ display: `${loading ? 'block' : 'none'}` }}
                        className="c-progress-upload__value overline">{progress[file.name] || 0}%</div>
                     <div
                        style={{ display: `${loading ? 'block' : 'none'}` }}
                        className="c-progress-upload__bar">
                        <div className="c-progress-upload__bar__status" style={{ width: `${progress[file.name] || 0}%` }} />
                     </div>
                     <div className="c-progress-upload__actions">
                        <button type="button" disabled={loading} onClick={() => removeItem(file.id, file.src)}>
                           <img src={Close} />
                        </button>
                     </div>
                     <div className="c-progress-upload__preview">
                        <img
                           src={file.type === "application/pdf" ? PDFIcon : file.src}
                           width="75"
                           height="75"
                           onClick={() => openModal(file)}
                        />
                     </div>
                  </div>
               )
            }
         })}
         <Modal
            title={selected.name || ''}
            visible={visible}
            onClose={() => setVisible(false)}
            className="file-modal"
         >
            <span className="small">{selected.size}</span>
            {
               selected.type === 'application/pdf'
                  ? <embed src={selected.src} className='file-embed file-pdf' />
                  : <img src={selected.src} className='file-embed' />
            }
         </Modal>
      </>
   )
}
export default UploadItem;
